import { apiClient } from '@/auth';
import { withChangeContext, type ChangeContext } from '@/lib/changeContext';

/**
 * Full meal as returned by `GET /patients/{id}/meals` and `GET /patients/{id}/meals/{meal_id}`.
 * The id field is `id` per swagger.yaml:17303 (MealResponse).
 */
export type Meal = {
  id: number;
  patient_id?: number;
  meal_date: string;
  meal_time: string;
  meal_name: MealName;
  meal_items?: MealItem[];
  meal_photo_id?: number | null;
  meal_photo_resized_url?: string | null;
  meal_photo_original_url?: string | null;
  is_deleted?: boolean;
  /** Meal-level glucose-response flags — true when the patient's CGM
   *  data around this meal fell into the corresponding range. Populated
   *  by the server when CGM is available; absent otherwise. Used only
   *  by the Logs filter card; safe to ignore when missing. */
  glucose_response_low?: boolean | null;
  glucose_response_high?: boolean | null;
  glucose_response_in_range?: boolean | null;
  glucose_response_unknown?: boolean | null;
};

export type MealName = 'breakfast' | 'lunch' | 'dinner' | 'snack';
export const MEAL_NAMES: MealName[] = ['breakfast', 'lunch', 'dinner', 'snack'];

/**
 * Full meal item per MealItemResponse (swagger.yaml:17802). The id is `id`,
 * not `meal_item_id`. The table-row response (ClinicianMealLogsTableResponseRow)
 * uses `meal_id` for the parent — that's a different type, see MealRow below.
 */
export type MealItem = {
  id: number;
  patient_id?: number;
  food_name: string;
  food_name_alias?: string | null;
  food_photo_url?: string | null;
  serving_unit_label: string;
  serving_unit_amount: number;
  servings?: number;
  percent_eaten?: number;
  addons?: string[];
  extra_addons?: string[];
  note?: string | null;
  preparation_method?: string | null;
  /** Required field on MealItemResponse — used for kcal totals. */
  energy_kcal?: number;
  carbohydrate_g?: number;
  /** Optional macro fields (per MealItemResponse, swagger.yaml:17223) — V2
   *  surfaces them so per-meal stacked bars (Logs v3 Nutrition-by-Meal card)
   *  can sum macros without a second nutrition_details_table call. */
  protein_g?: number;
  fat_g?: number;
  fiber_g?: number;
  /** Alcohol grams. Required on MealItemResponse per swagger:17475. Used
   *  by Logs v3 to render alcohol as its own kcal-contributing stack
   *  segment (7 kcal/g) alongside carbs/fat/protein — fibre is rendered
   *  as a hatched sub-segment within the carbs block since it's a
   *  carbohydrate sub-component, not an independent kcal source. */
  alcohol_g?: number;
  /** Sub-items chosen as add-ons to this meal item (e.g. butter on toast,
   *  dressing on a salad). Returned by the server alongside the meal-item
   *  payload — MealItemCustomAddonResponse, swagger:18244. The Logs
   *  timeline renders each as an indented sub-row under its parent item. */
  custom_addons?: MealItemCustomAddon[] | null;
  /** Per-item dietary classification flags (swagger:18050) — boolean
   *  cc_redmeat / cc_healthy_fat / etc. Used by the Logs filter card to
   *  match meals against nutrition criteria client-side. Modeled as a
   *  loose record because the schema grows over time and we don't want
   *  TypeScript to fall behind every new flag. */
  cc_columns?: Record<string, boolean | null | undefined> | null;
};

/**
 * Custom add-on attached to a meal item — subset of swagger:18244 that V2
 * actually consumes (display name + per-serving macros). Anything beyond
 * this is fetched only by the full meal-detail dialog when it cares.
 */
export type MealItemCustomAddon = {
  id?: number;
  food_name: string;
  food_name_alias?: string | null;
  serving_unit_label?: string;
  serving_unit_amount?: number;
  servings?: number;
  energy_kcal?: number;
  carbohydrate_g?: number;
  protein_g?: number;
  fat_g?: number;
  fiber_g?: number;
};

/** Row shape from `GET /patients/{id}/meals_table` — distinct from Meal. */
export type MealRow = {
  meal_id: number;
  meal_date: string;
  meal_time: string;
  meal_name: string;
  day_of_week?: string;
  meal_photo_id?: string | null;
  meal_photo_url?: string | null;
  food_name_with_image?: string | null;
  food_image_url?: string | null;
  /** Strings on the table endpoint (food name summaries), not full MealItem objects. */
  meal_items?: string[];
};

export type MealsTablePage = {
  rows: MealRow[];
  has_more: boolean;
  total_rows?: number;
  next?: string;
};

export type MealListParams = {
  from?: string;
  to?: string;
};

export type MealsTableParams = {
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
  order?: string;
};

export type CreateMealInput = {
  meal_date: string;
  meal_time: string;
  meal_name: MealName;
  meal_items?: MealItemInput[];
};

export type UpdateMealInput = {
  meal_date: string;
  meal_time: string;
  meal_name: MealName;
};

/**
 * Minimum payload to create or update a meal item. The full swagger schema has
 * many optional nutrient/cc/cognitive fields; V2 fills required defaults
 * (`addons`, `extra_addons`) and leaves the rest unset, matching what v1 does
 * for clinician-driven edits.
 */
/**
 * Writable custom add-on (topping) for a meal item — the subset of MNT's
 * `MealItemCustomAddonRequest` a clinician edits. `id` is present for an
 * existing add-on (so the server updates it in place) and omitted for a new
 * one. The server recomputes nutrients from the food match, so we send only
 * the identity + serving fields.
 */
export type MealItemCustomAddonInput = {
  id?: number;
  food_name: string;
  serving_unit_label: string;
  serving_unit_amount: number;
  servings: number;
};

export type MealItemInput = {
  food_name: string;
  food_name_alias?: string | null;
  serving_unit_label: string;
  serving_unit_amount: number;
  servings?: number;
  percent_eaten?: number;
  addons?: string[];
  extra_addons?: string[];
  note?: string | null;
  /**
   * Rich toppings. The meal-item PUT is a FULL REPLACE: whatever list you send
   * becomes the item's complete add-on set. Always round-trip the item's
   * CURRENT add-ons on every save — do NOT rely on omitting the field to
   * preserve them (omitting leaves the key off the payload, and the
   * full-replace endpoint drops the existing add-ons). Pass `[]` to
   * intentionally clear.
   */
  custom_addons?: MealItemCustomAddonInput[];
};

const normaliseItem = (item: MealItemInput) => ({
  ...item,
  addons: item.addons ?? [],
  extra_addons: item.extra_addons ?? [],
  servings: item.servings ?? 1,
  percent_eaten: item.percent_eaten ?? 1,
  // Full-replace field: include the caller's list as-is (an empty array clears
  // all add-ons). `undefined` leaves the key off the payload — callers must
  // round-trip existing add-ons rather than rely on omission to preserve them.
  ...(item.custom_addons
    ? { custom_addons: item.custom_addons.map((ca) => ({ ...ca, servings: ca.servings ?? 1 })) }
    : {}),
});

/**
 * Result shape of `GET /patients/{id}/meals_since_last_report` (MNT
 * swagger:3999). Used by the Generate-Report dialog to seed the date
 * range to the window of dates with logged meals since the last report
 * was sent — that's the clinically meaningful "fresh data" window.
 */
export type MealsSinceLastReport = {
  meal_dates: string[];
};

/**
 * Complete per-item nutrition (UsdaNutritionResponse, swagger:17720) attached
 * to disaggregated meal items. Values are the item's applied/consumed amounts,
 * so summing across a meal's items gives the meal's totals. All nullable.
 */
export type DisaggregatedNutrition = {
  energy_kcal?: number | null;
  protein_g?: number | null;
  fat_g?: number | null;
  carbohydrate_g?: number | null;
  fiber_g?: number | null;
  netcarb_g?: number | null;
  sodium_mg?: number | null;
  potassium_mg?: number | null;
  calcium_mg?: number | null;
  fattyacids_totalsaturated_g?: number | null;
  fattyacids_totaltrans_g?: number | null;
};

export type DisaggregatedMealItem = {
  id: number;
  food_name: string;
  /** Complete nutrition lives here on the "complete" response. */
  nutrition_response?: DisaggregatedNutrition | null;
};

/** Response from GET /patients/{id}/meals/{mealId}/disaggregated_meal. */
export type DisaggregatedMeal = {
  id: number;
  patient_id: number;
  meal_date: string;
  meal_time: string;
  meal_name: MealName;
  meal_items: DisaggregatedMealItem[];
};

export const mealsApi = {
  /**
   * Disaggregated items for one meal (composites split into component foods,
   * with complete nutrition). Server Redis-caches per meal. Used to compute the
   * Summary nutrient-distribution graphs from the same data the report uses.
   */
  async disaggregatedMeal(patientId: number, mealId: number): Promise<DisaggregatedMeal> {
    const res = await apiClient.get<DisaggregatedMeal>(
      `/patients/${patientId}/meals/${mealId}/disaggregated_meal`,
    );
    return res.data;
  },

  async sinceLastReport(patientId: number): Promise<MealsSinceLastReport> {
    try {
      const res = await apiClient.get<MealsSinceLastReport>(
        `/patients/${patientId}/meals_since_last_report`,
      );
      const data = res.data;
      // Server returns an object; guard so a 200 with weird payload still
      // resolves to a usable empty list rather than throwing in the UI.
      return Array.isArray(data?.meal_dates) ? data : { meal_dates: [] };
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status !== undefined && status >= 400 && status < 500) {
        return { meal_dates: [] };
      }
      throw err;
    }
  },

  async list(patientId: number, params?: MealListParams) {
    const res = await apiClient.get<Meal[]>(`/patients/${patientId}/meals`, {
      params: {
        date_since: params?.from,
        date_until: params?.to,
      },
    });
    return res.data;
  },

  async table(patientId: number, params?: MealsTableParams) {
    const res = await apiClient.get<MealsTablePage>(`/patients/${patientId}/meals_table`, {
      params: {
        date_since: params?.from,
        date_until: params?.to,
        limit: params?.limit,
        offset: params?.offset,
        order: params?.order,
      },
    });
    return res.data;
  },

  async get(patientId: number, mealId: number) {
    const res = await apiClient.get<Meal>(`/patients/${patientId}/meals/${mealId}`);
    return res.data;
  },

  async create(patientId: number, body: CreateMealInput): Promise<Meal> {
    const payload = {
      ...body,
      meal_items: body.meal_items?.map(normaliseItem),
    };
    const res = await apiClient.post<Meal>(`/patients/${patientId}/meals`, payload);
    return res.data;
  },

  async update(patientId: number, mealId: number, body: UpdateMealInput): Promise<Meal> {
    const res = await apiClient.put<Meal>(`/patients/${patientId}/meals/${mealId}`, body);
    return res.data;
  },

  async deleteMeal(patientId: number, mealId: number, changeCtx?: ChangeContext) {
    await apiClient.delete(
      `/patients/${patientId}/meals/${mealId}`,
      changeCtx ? withChangeContext(changeCtx) : undefined,
    );
  },

  async addItems(patientId: number, mealId: number, items: MealItemInput[]): Promise<MealItem[]> {
    const body = items.map(normaliseItem);
    const res = await apiClient.post(`/patients/${patientId}/meals/${mealId}/meal_items`, body);
    const data = res.data as MealItem[] | { meal_items?: MealItem[] };
    if (Array.isArray(data)) return data;
    return Array.isArray(data?.meal_items) ? data.meal_items : [];
  },

  async updateItem(
    patientId: number,
    mealId: number,
    mealItemId: number,
    body: MealItemInput,
  ): Promise<MealItem> {
    const res = await apiClient.put<MealItem>(
      `/patients/${patientId}/meals/${mealId}/meal_items/${mealItemId}`,
      normaliseItem(body),
    );
    return res.data;
  },

  async deleteMealItem(
    patientId: number,
    mealId: number,
    mealItemId: number,
    changeCtx?: ChangeContext,
  ) {
    await apiClient.delete(
      `/patients/${patientId}/meals/${mealId}/meal_items/${mealItemId}`,
      changeCtx ? withChangeContext(changeCtx) : undefined,
    );
  },
};
